import { observer } from 'mobx-react-lite'
import { ReactElement, useState } from 'react'
import Tree, { ItemId } from 'tree-dnd'
import { useFiles as useFinderFiles } from 'hooks/queries/finder/useFiles'
import { useFiles as useWorkspaceFiles } from 'hooks/queries/files/useFiles'
import { useActiveFolder } from 'hooks/useActiveFolder'
import { TreeRow } from './TreeRow/TreeRow'
import { useFilesTreeStore } from './stores/FilesTreeStore'
import { toTree } from './treeUtils'
import { reportError } from 'utils/error'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { Loading } from './Loading/Loading'
import { toVirtualMarketerTreeItem, isVirtualMarketerRootFolder, VM_ROOT_FOLDERS_QUERY } from './vmFiles'

interface Props {
  workspaceId: number
  location: string
  emptyState?: ReactElement
  isVirtualMarketer?: boolean
  useFinderQuery?: boolean
  queryOptions?: {
    items: number
  }
}

const FilesTree = observer(
  ({ workspaceId, location, emptyState, isVirtualMarketer = false, useFinderQuery = true, queryOptions }: Props) => {
    const [isInitialFetch, setIsInitialFetch] = useState(true)
    const treeStore = useFilesTreeStore()
    const items = queryOptions?.items || 50
    const finderFilesQuery = useFinderFiles(
      {
        location,
        workspaceId,
        query: { items },
        disableSchema: true,
      },
      {
        enabled: useFinderQuery,
        onSuccess: (data) => {
          setIsInitialFetch(false)
          // onSuccess also fires for cache writes (e.g. mutation rollbacks) that can
          // carry undefined data in react-query v3 — early-return so the rendered
          // tree survives. Pages can be error-shaped responses (request-dot-js
          // resolves network failures instead of throwing), so guard their contents.
          if (!data?.pages) return
          const files = data.pages.flatMap((page) => page.data?.files ?? [])
          treeStore.setTree(toTree(files, []))
        },
        onError: (error) => {
          setIsInitialFetch(false)
          reportError(error)
          showErrorMessageToast('There was an error fetching your files.')
        },
      },
    )
    const virtualMarketerFilesQuery = useWorkspaceFiles(
      {
        items,
        q: VM_ROOT_FOLDERS_QUERY,
      },
      {
        enabled: isVirtualMarketer,
        onSuccess: (data) => {
          setIsInitialFetch(false)
          if (!data?.pages) return
          const files = data.pages
            .flatMap((page) => page.data?.almanac_files ?? [])
            .filter(isVirtualMarketerRootFolder)
            .map((file) => toVirtualMarketerTreeItem(file, workspaceId))
          treeStore.setTree(toTree(files, []))
        },
        onError: (error) => {
          setIsInitialFetch(false)
          reportError(error)
          showErrorMessageToast('There was an error fetching your files.')
        },
      },
    )

    const hasEnabledQuery = useFinderQuery || isVirtualMarketer
    const filesQuery = isVirtualMarketer ? virtualMarketerFilesQuery : finderFilesQuery
    const { fetchNextPage, hasNextPage } = filesQuery
    const activeFolder = useActiveFolder()
    const [selectedRows, setSelectedRows] = useState<ItemId>()

    if (!hasEnabledQuery) return null
    if (!treeStore.tree) return null
    if (isInitialFetch) return <Loading />
    if (emptyState && treeStore.isEmpty) return emptyState

    return (
      <Tree
        tree={treeStore.tree}
        renderItem={(props) => (
          <TreeRow
            {...props}
            isLast={treeStore.isLast(props.item.id)}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            hasMore={hasNextPage}
            loadMore={fetchNextPage}
            updateItem={treeStore.updateItem}
            onRemoveItem={treeStore.removeItem}
            activeFolder={activeFolder}
          />
        )}
        isDragEnabled={false}
        isNestingEnabled={false}
        renderDropPlaceholder={(_) => <></>}
      />
    )
  },
)

export default FilesTree
